"use client";

import React, { useEffect, useRef } from "react";

declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}

type Props = {
  adClient?: string;
  adSlot?: string | number;
  firstDelay?: number;
  refreshInterval?: number;
  adTest?: boolean;
  onlyWhenVisible?: boolean;
};

export default function SidebarAd1560({
  adClient = "ca-pub-8612844749010589",
  adSlot = "",
  firstDelay = 30000,
  refreshInterval = 30000,
  adTest = true,
  onlyWhenVisible = true,
}: Props) {
  const insRef = useRef<HTMLModElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const intervalIdRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const pushAd = () => {
      if (cancelled || !insRef.current) return;

      try {
        // Only push if not already pushed
        if (!insRef.current.getAttribute("data-adsbygoogle-status")) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        //   console.log("[BuilderSidebarAd] Ad pushed at", new Date().toLocaleTimeString());
        }
      } catch (err) {
        console.warn("[BuilderSidebarAd] Push error:", err);
      }
    };

    const refreshAd = () => {
      if (cancelled || !containerRef.current || !insRef.current) return;

      // Remove old ins
      const oldIns = insRef.current;
      if (oldIns && oldIns.parentNode) {
        oldIns.parentNode.removeChild(oldIns);
      }

      // Create new ins
      const newIns = document.createElement("ins");
      newIns.className = "adsbygoogle";
      newIns.style.display = "inline-block";
      newIns.style.width = "300px";
      newIns.style.height = "250px";
      newIns.setAttribute("data-ad-client", adClient);
      newIns.setAttribute("data-ad-slot", String(adSlot));
      newIns.setAttribute("data-ad-format", "fixed");
      if (adTest) newIns.setAttribute("data-adtest", "on");

      containerRef.current.appendChild(newIns);
      insRef.current = newIns;

      pushAd();
    };

    const startAdCycle = () => {
      // First load after delay
      timeoutIdRef.current = window.setTimeout(() => {
        if (onlyWhenVisible && containerRef.current) {
          const observer = new IntersectionObserver(
            (entries) => {
              if (entries[0]?.isIntersecting) {
                pushAd();
                observer.disconnect();

                // Start refresh interval
                intervalIdRef.current = window.setInterval(
                  refreshAd,
                  Math.max(60000, refreshInterval)
                );
              }
            },
            { threshold: 0.01 }
          );
          if (containerRef.current) observer.observe(containerRef.current);
        } else {
          pushAd();
          // Start refresh interval
          intervalIdRef.current = window.setInterval(
            refreshAd,
            Math.max(60000, refreshInterval)
          );
        }
      }, firstDelay);
    };

    startAdCycle();

    return () => {
      cancelled = true;
      if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
      if (intervalIdRef.current) window.clearInterval(intervalIdRef.current);
    };
  }, [adClient, adSlot, firstDelay, refreshInterval, adTest, onlyWhenVisible]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "300px",
        height: "250px",
        minWidth: "300px",
        minHeight: "250px",
        maxWidth: "300px",
        maxHeight: "250px",
        overflow: "hidden",
        display: "block",
        position: "relative",
      }}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{
          display: "inline-block",
          width: "300px",
          height: "250px",
        }}
        data-ad-client={adClient}
        data-ad-slot={String(adSlot)}
        data-ad-format="fixed"
        data-adtest={adTest ? "on" : undefined}
      />
    </div>
  );
}